import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/request';
import type {
  WorkflowRule,
  WorkflowRuleListResponse,
} from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/api/workflowRule.types';
import { MoveWithAutomationModal } from './MoveWithAutomationModal';
import {
  deriveCurrentStageStopping,
  ruleActionsToStoppingActions,
  type StoppingAction,
} from './deriveCurrentStageStopping';
import { detailsFromRule } from './detailsFromRule';
import {
  useEnqueueAutomationMove,
  detailsFromNextAction,
  type EnqueueAutomationMoveInput,
  type WorkflowExecutionNextAction,
} from '@/components/automation/UnderwayPopup';

// The first (lowest-position) action's delay in seconds, or 0 when the rule has
// no actions. Drives the "scheduled" / "Sends in X" copy for a delayed first
// step — the move response's `next_action.delay_seconds` is preferred; this is
// the fallback when the response carried none.
const firstStepDelaySeconds = (rule: WorkflowRule): number =>
  [...(rule.actions ?? [])].sort((a, b) => a.position - b.position)[0]
    ?.delay_seconds ?? 0;

// What `performMove` resolves to. The move endpoint returns the
// pending stage-automation execution IDs it created synchronously, so
// the popup can run the undo window without racing Sidekiq. Callers
// that don't surface IDs (or "Move without automation") return void.
//
// `firstStepAlreadyRan` — set from the move response's
// `next_action` (see `firstStepAlreadyRan`): true on stage RE-ENTRY where the
// immediate first activity already ran, so the popup must not re-enqueue it (the
// BE would `already_created`-skip it anyway).
//
// `nextAction` — the move response's `next_action` (enriched by the BE with
// interview mode / survey audience / reviewer recipients). The popup prefers
// these full per-kind details over the client-side `detailsFromRule` fallback.
export type MoveResult = {
  pendingExecutionIds?: number[];
  firstStepAlreadyRan?: boolean;
  nextAction?: WorkflowExecutionNextAction;
};

export type PerformMove = (
  skipAutomation: boolean
) => Promise<MoveResult | void> | MoveResult | void;

// Metadata the confirm hook needs to enqueue the post-move trigger
// window into the popup. Only consulted on the "Move" (with
// automation) path — "Move without automation" doesn't create
// executions on BE, so the popup is irrelevant.
//
// `getEnqueueInputBase(rule)` returns everything except executionIds
// — those come back from `performMove`'s MoveResult.
export type ConfirmAndMoveUnderway = {
  getEnqueueInputBase: (
    rule: WorkflowRule
  ) => Omit<EnqueueAutomationMoveInput, 'executionIds'> | null;
};

export type ConfirmAndMoveArgs = {
  targetStageId: number | string;
  targetStageName: string;
  currentStageName?: string;
  currentStageStoppingActions?: StoppingAction[];
  applicationId?: number | string;
  performMove: PerformMove;
  // Optional — if provided, enables the popup-delayed move path.
  underway?: ConfirmAndMoveUnderway;
  // Skip the confirm modal and proceed as if the user clicked "Move"
  // (with automation) — used by rapid flows like Quick App Review.
  autoConfirm?: boolean;
};

type ModalState = {
  isOpen: boolean;
  targetStageName: string;
  targetRule: WorkflowRule | null;
  currentStageName?: string;
  currentStageStoppingActions: StoppingAction[];
  performMove: PerformMove | null;
  underway: ConfirmAndMoveUnderway | null;
};

const stageRuleKey = (stageId: string) =>
  ['workflowRule', 'stage', stageId] as const;

const fetchActiveStageRule = async (
  stageId: string
): Promise<WorkflowRule | null> => {
  const res = await api.get<WorkflowRuleListResponse>('workflow_rules', {
    params: {
      event_name: 'application.stage_changed',
      ats_job_stage_id: Number(stageId),
      status: 'active',
    },
  });
  return res.workflow_rules[0] ?? null;
};

type ApplicationShowResponse = {
  job_stage_id?: number | string;
  ats_job_stages?: unknown[];
};

const fetchApplicationForMove = async (
  applicationId: number | string
): Promise<ApplicationShowResponse | null> => {
  try {
    return await api.get<ApplicationShowResponse>(
      `ats_applications/${applicationId}`,
      { params: { include: 'stage_actions' } }
    );
  } catch {
    return null;
  }
};

export const useMoveStageWithAutomationConfirm = () => {
  const queryClient = useQueryClient();
  const enqueueAutomationMove = useEnqueueAutomationMove();
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    targetStageName: '',
    targetRule: null,
    currentStageStoppingActions: [],
    performMove: null,
    underway: null,
  });
  const [isMoving, setIsMoving] = useState(false);

  const closeModal = useCallback(() => {
    setModal((s) => ({
      ...s,
      isOpen: false,
      performMove: null,
      underway: null,
    }));
  }, []);

  const confirmAndMove = useCallback(
    async ({
      targetStageId,
      targetStageName,
      currentStageName,
      currentStageStoppingActions = [],
      applicationId,
      performMove,
      underway,
      autoConfirm = false,
    }: ConfirmAndMoveArgs) => {
      const stageIdString = String(targetStageId);
      const rule = await queryClient.fetchQuery({
        queryKey: stageRuleKey(stageIdString),
        queryFn: () => fetchActiveStageRule(stageIdString),
      });

      if (!rule) {
        await performMove(false);
        return;
      }

      // Auto-confirm: move with automation + enqueue the undo popup,
      // never opening the modal.
      if (autoConfirm) {
        const result = await performMove(false);
        // Enqueue the undo popup for BOTH immediate and delayed first steps —
        // after the 15s window the popup calls `/trigger`, and the BE honors a
        // delayed action's delay by re-scheduling it (`ExecuteWorker`), so it
        // never fires early. On stage re-entry where the first step already ran,
        // skip it (BE would `already_created`-skip it and it must not re-show).
        if (underway && !result?.firstStepAlreadyRan) {
          const base = underway.getEnqueueInputBase(rule);
          const executionIds = result?.pendingExecutionIds ?? [];
          if (base && executionIds.length > 0) {
            const delaySeconds =
              result?.nextAction?.delay_seconds ?? firstStepDelaySeconds(rule);
            // Prefer the BE `next_action` details (full per-kind: interview mode,
            // survey audience, reviewer recipients); fall back to the rule
            // (interview mode only) if the move response carried no next_action.
            enqueueAutomationMove({
              ...base,
              details: result?.nextAction
                ? detailsFromNextAction(result.nextAction)
                : detailsFromRule(rule),
              executionIds,
              delayed: delaySeconds > 0,
              delaySeconds: delaySeconds > 0 ? delaySeconds : undefined,
            });
          }
        }
        return;
      }

      let derivedCurrentStageName = currentStageName;
      let derivedStoppingActions = currentStageStoppingActions;
      if (applicationId !== undefined && applicationId !== null) {
        const application = await fetchApplicationForMove(applicationId);
        if (application) {
          const derived = deriveCurrentStageStopping(application);
          derivedCurrentStageName = derived.currentStageName;
          derivedStoppingActions = derived.currentStageStoppingActions;

          // Nothing in flight — fall back to the current stage's
          // active rule so its automation still shows in the modal.
          const currentStageId = application.job_stage_id;
          if (
            derivedStoppingActions.length === 0 &&
            currentStageId !== undefined &&
            currentStageId !== null
          ) {
            const currentRule = await queryClient.fetchQuery({
              queryKey: stageRuleKey(String(currentStageId)),
              queryFn: () => fetchActiveStageRule(String(currentStageId)),
            });
            if (currentRule) {
              derivedStoppingActions =
                ruleActionsToStoppingActions(currentRule);
            }
          }
        }
      }

      setModal({
        isOpen: true,
        targetStageName,
        targetRule: rule,
        currentStageName: derivedCurrentStageName,
        currentStageStoppingActions: derivedStoppingActions,
        performMove,
        underway: underway ?? null,
      });
    },
    [queryClient, enqueueAutomationMove]
  );

  const runMove = useCallback(
    async (skipAutomation: boolean) => {
      if (!modal.performMove) return;
      setIsMoving(true);
      try {
        // Always fire the move first. BE moves the candidate(s)
        // immediately; for stage rules BE also creates the pending
        // workflow_executions synchronously and returns their IDs.
        const result = await modal.performMove(skipAutomation);

        // Move WITH automation + underway metadata + active rule
        // → enqueue the IDs BE returned so the popup can run the 15s
        // trigger/cancel window (immediate or delayed first step — the BE
        // re-schedules a delayed action, so triggering never fires it early).
        // Skip on re-entry where the first step already ran (BE
        // `already_created`-skips it).
        if (
          !skipAutomation &&
          modal.underway &&
          modal.targetRule &&
          !result?.firstStepAlreadyRan
        ) {
          const base = modal.underway.getEnqueueInputBase(modal.targetRule);
          const executionIds = result?.pendingExecutionIds ?? [];
          if (base && executionIds.length > 0) {
            const delaySeconds =
              result?.nextAction?.delay_seconds ??
              firstStepDelaySeconds(modal.targetRule);
            // Prefer the BE `next_action` details (full per-kind); fall back to
            // the rule (interview mode only) when the response had no next_action.
            enqueueAutomationMove({
              ...base,
              details: result?.nextAction
                ? detailsFromNextAction(result.nextAction)
                : detailsFromRule(modal.targetRule),
              executionIds,
              delayed: delaySeconds > 0,
              delaySeconds: delaySeconds > 0 ? delaySeconds : undefined,
            });
          }
        }
        closeModal();
      } finally {
        setIsMoving(false);
      }
    },
    [modal, closeModal, enqueueAutomationMove]
  );

  const modalElement = (
    <MoveWithAutomationModal
      isOpen={modal.isOpen}
      onClose={closeModal}
      targetStageName={modal.targetStageName}
      targetRule={modal.targetRule}
      currentStageName={modal.currentStageName}
      currentStageStoppingActions={modal.currentStageStoppingActions}
      isMoving={isMoving}
      onCancel={closeModal}
      onMoveWithoutAutomation={() => void runMove(true)}
      onMove={() => void runMove(false)}
    />
  );

  return { confirmAndMove, modalElement };
};
